import React, { useState, useEffect } from 'react';
import { CloudRain, RefreshCw, AlertCircle, Search, Map as MapIcon } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, LayersControl, LayerGroup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const getWeatherDescription = (code) => {
    if (code == null) return '-';
    const wmoCodes = {
        0: 'Clear sky', 1: 'Mainly clear', 2: 'Partly cloudy', 3: 'Overcast',
        45: 'Fog', 48: 'Rime fog', 51: 'Light drizzle', 53: 'Moderate drizzle', 
        55: 'Dense drizzle', 56: 'Light freezing drizzle', 57: 'Dense freezing drizzle',
        61: 'Slight rain', 63: 'Moderate rain', 65: 'Heavy rain', 66: 'Light freezing rain', 
        67: 'Heavy freezing rain', 71: 'Slight snow', 73: 'Moderate snow', 75: 'Heavy snow', 
        77: 'Snow grains', 80: 'Slight rain showers', 81: 'Moderate rain showers', 
        82: 'Violent rain showers', 85: 'Slight snow showers', 86: 'Heavy snow showers', 
        95: 'Thunderstorm', 96: 'Thunderstorm with slight hail', 99: 'Thunderstorm with heavy hail'
    };
    return wmoCodes[code] || `Code ${code}`;
};

const MapBounds = ({ sites }) => {
    const map = useMap();
    useEffect(() => {
        if (sites && sites.length > 0) {
            const validSites = sites.filter(s => s.latitude && s.longitude);
            if (validSites.length > 0) {
                const bounds = L.latLngBounds(validSites.map(s => [s.latitude, s.longitude]));
                if (bounds.isValid()) {
                    map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 12, duration: 1.5 });
                }
            }
        }
    }, [sites, map]);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (map) map.invalidateSize();
        }, 150);
        return () => clearTimeout(timer);
    }, [map]);

    return null;
};

const WeatherData = ({ selectedClient, clientColorMap = {} }) => {
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'map'

    const fetchWeather = async () => {
        setLoading(true);
        setError(null);
        try {
            const clientQuery = selectedClient !== 'All Clients' ? `?clientName=${encodeURIComponent(selectedClient)}` : '';
            const response = await fetch(`/api/weather${clientQuery}`);
            if (!response.ok) throw new Error('Failed to fetch weather data');
            const data = await response.json();
            setSites(data.sites || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const body = selectedClient !== 'All Clients' ? JSON.stringify({ clientName: selectedClient }) : JSON.stringify({});
            const response = await fetch('/api/weather/refresh', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body
            });
            if (!response.ok) throw new Error('Failed to refresh weather data');
            const data = await response.json();
            setSites(data.sites || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchWeather();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClient]);

    const filteredSites = sites.filter(site => 
        (site.site_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (site.address || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex flex-col h-full w-full bg-gray-50 overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-4 md:py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shrink-0">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                        Weather data
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Live weather conditions for {selectedClient}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 md:gap-4 w-full md:w-auto">
                    <div className="relative w-full md:w-auto">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input 
                            type="text" 
                            placeholder="Search sites..." 
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        />
                    </div>
                    <div className="flex bg-gray-100 p-1 rounded-lg w-full md:w-auto justify-center">
                        <button 
                            onClick={() => setViewMode('table')}
                            className={`flex-1 md:flex-none px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'table' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Table View
                        </button>
                        <button 
                            onClick={() => setViewMode('map')}
                            className={`flex-1 md:flex-none justify-center px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${viewMode === 'map' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <MapIcon size={16} /> Map View
                        </button>
                    </div>
                    <button 
                        onClick={handleRefresh} 
                        disabled={refreshing || loading}
                        className="w-full md:w-auto justify-center flex items-center gap-2 bg-[#76AB3F] hover:bg-[#659733] text-white px-5 py-2 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-70"
                    >
                        <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
                        {refreshing ? 'Refreshing...' : 'Refresh Live Weather'}
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-auto p-8 relative">
                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-3 mb-6 border border-red-200">
                        <AlertCircle />
                        <span>{error}</span>
                    </div>
                )}

                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                        <RefreshCw size={40} className="animate-spin mb-4 text-[#76AB3F]" />
                        <p>Loading weather data...</p>
                    </div>
                ) : filteredSites.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <CloudRain size={48} className="mb-4 text-gray-300" />
                        <p className="text-lg font-medium text-gray-500">No sites with weather data found.</p>
                        <p className="text-sm mt-1">Try uploading sites or clicking "Refresh Live Weather".</p>
                    </div>
                ) : viewMode === 'table' ? (
                    <>
                    <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-xs">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Site Name</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Location</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Temp (°F)</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Humidity (%)</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Wind (mph)</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Wind Gust (mph)</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Clouds (%)</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Irradiance (W/m²)</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Weather</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Snow (m)</th>
                                    <th className="px-3 py-3 text-left font-semibold text-gray-600 tracking-wider">Last Updated</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {filteredSites.map((site) => (
                                    <tr key={site.id} className="hover:bg-blue-50 transition-colors">
                                        <td className="px-3 py-3 whitespace-nowrap font-medium text-gray-800 truncate max-w-[180px]" title={site.site_name || 'N/A'}>{site.site_name || 'N/A'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-500 truncate max-w-[180px]" title={site.address || site.postal_code || 'N/A'}>{site.address || site.postal_code || 'N/A'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-800">
                                            {site.temperature != null ? (
                                                <span className={`font-semibold ${site.temperature > 85 ? 'text-red-500' : site.temperature < 40 ? 'text-blue-500' : 'text-orange-500'}`}>
                                                    {site.temperature}°F
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-600">{site.humidity != null ? `${site.humidity}%` : '-'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-600">{site.wind_speed != null ? `${site.wind_speed} mph` : '-'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-600">{site.wind_gust != null ? `${site.wind_gust} mph` : '-'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-600">{site.cloud_cover != null ? `${site.cloud_cover}%` : '-'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-600">{site.irradiance != null ? `${site.irradiance} W/m²` : '-'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-600">{getWeatherDescription(site.weather_code)}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-600">{site.snow_cover != null ? `${site.snow_cover} m` : '-'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-[10px] text-gray-400">
                                            {site.last_weather_update ? new Date(site.last_weather_update).toLocaleString() : 'Never'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                        {filteredSites.map((site) => (
                            <div key={site.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col gap-3">
                                <div>
                                    <h3 className="font-bold text-gray-800 text-base" title={site.site_name || 'N/A'}>{site.site_name || 'N/A'}</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">{site.address || site.postal_code || 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-sm bg-gray-50 p-3 rounded-lg border border-gray-100">
                                    <div className="flex flex-col"><span className="text-gray-400 text-[10px] font-bold tracking-wider">Temp</span><span className={`font-semibold ${site.temperature > 85 ? 'text-red-500' : site.temperature < 40 ? 'text-blue-500' : 'text-orange-500'}`}>{site.temperature != null ? `${site.temperature}°F` : '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-gray-400 text-[10px] font-bold tracking-wider">Humidity</span><span className="font-semibold text-gray-700">{site.humidity != null ? `${site.humidity}%` : '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-gray-400 text-[10px] font-bold tracking-wider">Wind</span><span className="font-semibold text-gray-700">{site.wind_speed != null ? `${site.wind_speed} mph` : '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-gray-400 text-[10px] font-bold tracking-wider">Clouds</span><span className="font-semibold text-gray-700">{site.cloud_cover != null ? `${site.cloud_cover}%` : '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-gray-400 text-[10px] font-bold tracking-wider">Irradiance</span><span className="font-semibold text-gray-700">{site.irradiance != null ? `${site.irradiance}` : '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-gray-400 text-[10px] font-bold tracking-wider">Snow</span><span className="font-semibold text-gray-700">{site.snow_cover != null ? `${site.snow_cover}m` : '-'}</span></div>
                                </div>
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-indigo-500 font-medium bg-indigo-50 px-2 py-1 rounded">{getWeatherDescription(site.weather_code)}</span>
                                    <span className="text-gray-400">{site.last_weather_update ? new Date(site.last_weather_update).toLocaleString() : 'Never'}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    </>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col min-h-[500px]">
                        <MapContainer 
                            center={[39.8283, -98.5795]} 
                            zoom={4} 
                            minZoom={2}
                            maxBounds={[[-90, -180], [90, 180]]}
                            maxBoundsViscosity={1.0}
                            style={{ height: '100%', width: '100%' }} 
                            attributionControl={false}
                        >
                            <MapBounds sites={filteredSites} />
                            <LayersControl position="bottomright">
                                <LayersControl.BaseLayer name="Satellite">
                                    <LayerGroup>
                                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                                        <TileLayer url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" opacity={1} />
                                    </LayerGroup>
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Satellite (Old)">
                                    <LayerGroup>
                                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                                        <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/light_only_labels/{z}/{x}/{y}{r}.png" />
                                    </LayerGroup>
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer checked name="OpenStreetMap">
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" noWrap={true} />
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Light">
                                    <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" noWrap={true} />
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Terrain">
                                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}" />
                                </LayersControl.BaseLayer>
                            </LayersControl>
                            {filteredSites.map(site => {
                                if (!site.latitude || !site.longitude) return null;
                                const clientNameKey = (site.client_name || 'General').trim().toUpperCase();
                                const clientColor = clientColorMap[clientNameKey] || site.Color || site.color || '#76AB3F';
                                return (
                                    <CircleMarker 
                                        key={site.id} 
                                        center={[site.latitude, site.longitude]}
                                        radius={4}
                                        fillColor={clientColor}
                                        color="#ffffff"
                                        weight={1.5}
                                        fillOpacity={0.85}
                                    >
                                        <Tooltip direction="top" offset={[0, -10]} opacity={1} className="site-hover-tooltip" style={{ padding: 0, border: 'none', background: 'transparent', boxShadow: 'none' }}>
                                            <div style={{ fontFamily: "'Inter', sans-serif", minWidth: "220px", maxWidth: "260px", overflow: "hidden", borderRadius: "12px", background: "#fff", margin: "-6px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" }}>
                                                <div style={{ height: "3px", background: "linear-gradient(90deg, #76AB3F, #2563EB)" }}></div>
                                                <div style={{ padding: "12px 14px" }}>
                                                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#1a2e0a", marginBottom: "8px", lineHeight: 1.3, wordBreak: "break-word" }}>
                                                        {site.site_name || 'Site'}
                                                    </div>
                                                    
                                                    <div style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "#EAF4DB", border: "1px solid #C7E0A8", borderRadius: "20px", padding: "2px 9px", marginBottom: "10px" }}>
                                                        <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#76AB3F" }}></div>
                                                        <span style={{ fontSize: "10px", fontWeight: 600, color: "#4a7a28", letterSpacing: "0.04em" }}>{site.client_name || 'Client'}</span>
                                                    </div>

                                                    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                                                            <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500, flexShrink: 0 }}>Address</span>
                                                            <span style={{ fontSize: "11px", color: "#374151", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "150px" }} title={site.address || site.postal_code || '-'}>
                                                                {site.address || site.postal_code || '-'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #E5EDDB" }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                                                            <span style={{ fontSize: "11px", fontWeight: 700, color: "#2563EB", letterSpacing: "0.04em" }}>⚡ WEATHER DATA</span>
                                                        </div>
                                                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>🌡️ Temperature</span>
                                                                <span style={{ fontSize: "11px", color: "#C2410C", fontWeight: 700 }}>{site.temperature != null ? `${site.temperature} °F` : '-'}</span>
                                                            </div>
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>☁️ Cloud Cover</span>
                                                                <span style={{ fontSize: "11px", color: "#4B5563", fontWeight: 700 }}>{site.cloud_cover != null ? `${site.cloud_cover} %` : '-'}</span>
                                                            </div>
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>☀️ Irradiance</span>
                                                                <span style={{ fontSize: "11px", color: "#D97706", fontWeight: 700 }}>{site.irradiance != null ? `${site.irradiance} W/m²` : '-'}</span>
                                                            </div>
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>💨 Wind Speed</span>
                                                                <span style={{ fontSize: "11px", color: "#0369a1", fontWeight: 700 }}>{site.wind_speed != null ? `${site.wind_speed} mph` : '-'}</span>
                                                            </div>
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>🌪️ Wind Gust</span>
                                                                <span style={{ fontSize: "11px", color: "#1d4ed8", fontWeight: 700 }}>{site.wind_gust != null ? `${site.wind_gust} mph` : '-'}</span>
                                                            </div>
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>❄️ Snow Cover</span>
                                                                <span style={{ fontSize: "11px", color: "#374151", fontWeight: 700 }}>{site.snow_cover != null ? `${site.snow_cover} m` : '-'}</span>
                                                            </div>
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>🌤️ Conditions</span>
                                                                <span style={{ fontSize: "11px", color: "#374151", fontWeight: 700 }}>{getWeatherDescription(site.weather_code)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Tooltip>
                                    </CircleMarker>
                                );
                            })}
                        </MapContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeatherData;
